import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Motion";

const PILLARS = [
    {
        n: "01",
        tag: "Sourcing",
        h: (
            <>
                Paper with a <em className="italic">paper trail.</em>
            </>
        ),
        b: "Every reel we convert is FSC®-certified paperboard from mills we can name — ITC among them — with chain-of-custody documentation from fibre to finished bowl. If your auditors ask, we hand over the file.",
        img: "/generated/why_sourcing.png",
        alt: "Reels of FSC-certified kraft paperboard stacked in a paper mill warehouse — responsibly sourced paper for The Puro Pack bowls",
        to: "/sustainable-packaging",
        linkLabel: "Our materials",
    },
    {
        n: "02",
        tag: "Food Safety",
        h: (
            <>
                Food-grade. No exceptions, no <em className="italic">fine print.</em>
            </>
        ),
        b: "Every substrate and coating that touches your food is certified food-grade and run under strict hygiene controls — tested batch by batch, not once a year.",
        img: "/generated/why_foodsafety.png",
        alt: "Pristine white paper bowl filled with fresh salad — food-grade paper packaging by The Puro Pack",
        to: "/sustainable-packaging",
        linkLabel: "Our standards",
    },
    {
        n: "03",
        tag: "Impact",
        h: (
            <>
                Less material. Lighter footprint. Same <em className="italic">backbone.</em>
            </>
        ),
        b: "Our formats are engineered to cut material and waste without thinning performance — bowls that survive hot curries and delivery bikes, then leave the lightest possible mark.",
        img: "/generated/why_impact.png",
        alt: "Kraft paper bowl resting in garden soil with seedlings — lower-impact packaging by The Puro Pack",
        to: "/sustainable-packaging",
        linkLabel: "Sustainability",
    },
    {
        n: "04",
        tag: "Scale",
        h: (
            <>
                From first café to <em className="italic">five-hundredth</em> outlet.
            </>
        ),
        b: "Growing food brands, QSR chains and distributors get the same bowl, the same coating and the same lead time at any volume — manufacturing that scales with your menu, not against it.",
        img: "/generated/why_scale.png",
        alt: "Stacks of kraft paper bowls packed in shipping cartons on a factory floor — scalable manufacturing at The Puro Pack",
        to: "/industries",
        linkLabel: "Who we serve",
    },
];

export default function WhyPuroPack() {
    return (
        <section className="bg-bone" data-testid="why-puro-pack">
            <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <Reveal>
                    <div className="grid lg:grid-cols-12 gap-8 mb-6">
                        <div className="lg:col-span-4 font-mono text-xs uppercase tracking-widest text-stone">[ Why The Puro Pack ]</div>
                        <h2 className="lg:col-span-8 font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-forest">
                            Proof, not <em className="italic">promises.</em>
                        </h2>
                    </div>
                </Reveal>

                {PILLARS.map((p, i) => (
                    <Reveal key={p.n} delay={0.05}>
                        <div className="grid lg:grid-cols-12 gap-10 items-center border-t border-forest/30 py-14 lg:py-20" data-testid={`why-pillar-${p.n}`}>
                            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                <div className="image-frame aspect-[4/3]">
                                    <img src={p.img} alt={p.alt} loading="lazy" />
                                </div>
                            </div>
                            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                                <div className="font-mono text-xs uppercase tracking-widest text-clay mb-4">[ {p.n} · {p.tag} ]</div>
                                <h3 className="font-serif text-4xl lg:text-6xl leading-[1.02] tracking-tight text-forest">{p.h}</h3>
                                <p className="mt-6 text-inkgreen/80 leading-relaxed max-w-xl">{p.b}</p>
                                <Link
                                    to={p.to}
                                    className="inline-flex items-center gap-2 mt-8 font-mono text-xs uppercase tracking-widest text-forest hover:text-clay transition-colors"
                                    data-testid={`why-link-${p.n}`}
                                >
                                    {p.linkLabel} <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}
